/**
 * Wiki-link resolution — **at render time, from the page cache** (`070` §7.4).
 *
 * Read the argument in the spec before changing anything here; it is the load-bearing
 * decision of the whole link design.
 *
 * When `The Grey Vault` is finally created, the `page_link` ROWS resolve server-side — but
 * the `wikiLink` NODES inside the fifteen session pages that mention it still carry
 * `pageId: null`. **We do not go back and rewrite those documents.** Rewriting them would:
 *
 *  1. turn "create a page" — the cheapest, most frequent action in the app — into a write
 *     proportional to the size of the notebook, and
 *  2. bump `updated_at` on pages the user is not editing, which makes every open editor tab
 *     think it is stale and fires the `412 stale_write` recovery banner for a change the
 *     user never made. That is a data-loss-shaped bug caused by a cosmetic feature.
 *
 * Instead the renderer resolves against the tree the client already has in the TanStack
 * Query cache. It is a `useMemo` over data already in memory, it is idempotent, and it
 * self-heals: delete the page again and the links go back to broken, correctly, with no
 * cleanup pass.
 *
 * `wikiLink.pageId` therefore means "the id known **at authoring time**", not "the
 * authoritative link target". `page_link` is the authoritative graph, and it is what
 * backlinks read.
 */
import type { TreePage } from '@/api/endpoints/pages';

/** The two attrs of the node, and the only two (`031-content-format.md`). Do not add a third. */
export interface WikiLinkAttrs {
  pageId: string | null;
  label: string;
}

export interface ResolvedWikiLink {
  /** Where a click goes. `null` → the link is broken and a click offers to create the page. */
  targetId: string | null;
  /**
   * What the link SHOWS. For a resolved link this is the target's **current** title, not
   * the stored `label` — so renaming a page immediately updates every link to it, with no
   * document rewritten anywhere (`070` §7.5).
   */
  text: string;
  /** A first-class, expected state. Never an error, never red, never a warning icon (§7.2). */
  broken: boolean;
}

/** Two lookups over the tree, built once per tree — not once per link. */
export interface WikiLinkIndex {
  byTitle: ReadonlyMap<string, TreePage>;
  byId: ReadonlyMap<string, TreePage>;
}

function key(title: string): string {
  return title.trim().toLowerCase();
}

export function buildWikiLinkIndex(pages: readonly TreePage[]): WikiLinkIndex {
  const byTitle = new Map<string, TreePage>();
  const byId = new Map<string, TreePage>();

  for (const page of pages) {
    byId.set(page.id, page);
    // First writer wins, so resolution is stable when two pages share a title. The server's
    // own resolver (§7.4) is equally arbitrary in that case; what matters is that the client
    // does not flip between them from one render to the next.
    if (!byTitle.has(key(page.title))) byTitle.set(key(page.title), page);
  }

  return { byTitle, byId };
}

export function resolveWikiLink(attrs: WikiLinkAttrs, index: WikiLinkIndex): ResolvedWikiLink {
  const { label } = attrs;

  if (attrs.pageId !== null) {
    // A link authored against a real page is NEVER broken, even when the target is missing
    // from the tree — the tree may simply not have loaded yet, and it excludes archived
    // pages. Flashing "broken" on every boot, or the moment someone archives a page, would
    // make the broken state meaningless. Keep the id; fall back to the stored `label` for
    // the text (§7.5: this fallback is exactly what `label` is for).
    const page = index.byId.get(attrs.pageId);
    return { targetId: attrs.pageId, text: page?.title ?? label, broken: false };
  }

  // `pageId: null` — the user typed `[[The Grey Vault]]` for a page that did not exist.
  // THIS is where late resolution happens: the day the page is created, every link that was
  // waiting for it lights up, with nothing rewritten and no migration run.
  const page = index.byTitle.get(key(label));
  if (page !== undefined) return { targetId: page.id, text: page.title, broken: false };

  return { targetId: null, text: label, broken: true };
}
